import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Shield, AlertCircle, RefreshCw, ChevronLeft, ChevronRight, ArrowLeft, Bot, X } from 'lucide-react';
import {
  apiService,
  RateLimitViolation,
  RateLimitViolationStats,
} from '../../services/api';
import { toast } from 'sonner';

const VIOLATION_TYPE_LABELS: Record<string, string> = {
  message_rate: 'Nachrichtenrate',
  session_creation: 'Sitzungserstellung',
  concurrent_session: 'Gleichzeitige Sitzungen',
};

const VIOLATION_TYPE_COLORS: Record<string, string> = {
  message_rate: 'bg-orange-100 text-orange-800',
  session_creation: 'bg-red-100 text-red-800',
  concurrent_session: 'bg-purple-100 text-purple-800',
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function RateLimitLogsPage() {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const urlAgentId = urlSearchParams.get('agentId') || undefined;
  const agentName = (location.state as any)?.agentName as string | undefined;
  const backCustomerId = (location.state as any)?.customerId as string | undefined;
  const backCustomerName = (location.state as any)?.customerName as string | undefined;

  const isAgentView = !!urlAgentId;

  const [violations, setViolations] = useState<RateLimitViolation[]>([]);
  const [stats, setStats] = useState<RateLimitViolationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [violationType, setViolationType] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);

  const totalPages = Math.ceil(total / limit);

  const buildParams = useCallback(() => {
    return {
      agentId: urlAgentId,
      violationType: violationType !== 'all' ? violationType : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      page,
      limit,
    };
  }, [urlAgentId, violationType, startDate, endDate, page, limit]);

  const loadViolations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getRateLimitViolations(buildParams());
      setViolations(response.items);
      setTotal(response.total);
    } catch (err: any) {
      console.error('Failed to load violations:', err);
      setError('Verstöße konnten nicht geladen werden');
      toast.error('Fehler beim Laden der Rate-Limit-Verstöße');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const statsParams = {
        agentId: urlAgentId,
        violationType: violationType !== 'all' ? violationType : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };
      const data = await apiService.getRateLimitViolationStats(statsParams);
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [urlAgentId, violationType, startDate, endDate]);

  useEffect(() => {
    loadViolations();
  }, [loadViolations]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleApplyFilters = () => {
    setPage(1);
    loadViolations();
    loadStats();
  };

  const handleResetFilters = () => {
    setViolationType('all');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);
      if (page <= 3) end = 4;
      else if (page >= totalPages - 2) start = totalPages - 3;
      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  const handleClearAgentFilter = () => {
    setUrlSearchParams({});
  };

  const handleBackToAgent = () => {
    if (backCustomerId) {
      navigate(`/customers/${backCustomerId}/agent-config`);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="space-y-6">
      {isAgentView ? (
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Button variant="ghost" size="sm" onClick={handleBackToAgent}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Zurück
            </Button>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            Rate Limit Logs
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="flex items-center gap-1.5 px-3 py-1 text-sm">
              <Bot className="w-3.5 h-3.5" />
              {agentName || urlAgentId}
              {backCustomerName && <span className="text-gray-400 ml-1">({backCustomerName})</span>}
            </Badge>
            <Button variant="ghost" size="sm" onClick={handleClearAgentFilter} className="text-xs text-gray-500">
              <X className="w-3 h-3 mr-1" />
              Filter aufheben
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Rate Limit Logs
          </h1>
          <p className="text-gray-600">Gesamtübersicht aller Verstöße und Sperren</p>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Gesamt Verstöße</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            ) : (
              <div className="text-2xl font-bold text-gray-900">{stats?.total ?? 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Nach Typ</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            ) : (
              <div className="space-y-1">
                {stats?.byType && Object.entries(stats.byType).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between text-sm">
                    <Badge className={VIOLATION_TYPE_COLORS[type] ?? 'bg-gray-100 text-gray-800'}>
                      {VIOLATION_TYPE_LABELS[type] ?? type}
                    </Badge>
                    <span className="font-medium text-gray-900">{count}</span>
                  </div>
                ))}
                {(!stats?.byType || Object.keys(stats.byType).length === 0) && (
                  <span className="text-sm text-gray-500">Keine Daten</span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Top IPs</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            ) : (
              <div className="space-y-1">
                {stats?.topIps && stats.topIps.slice(0, 5).map(({ ip, count }) => (
                  <div key={ip} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-gray-700 truncate max-w-[120px]">{ip}</span>
                    <span className="font-medium text-gray-900 ml-2">{count}</span>
                  </div>
                ))}
                {(!stats?.topIps || stats.topIps.length === 0) && (
                  <span className="text-sm text-gray-500">Keine Daten</span>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="space-y-1">
              <Label htmlFor="violation-type">Verstoß-Typ</Label>
              <Select value={violationType} onValueChange={setViolationType}>
                <SelectTrigger id="violation-type">
                  <SelectValue placeholder="Alle Typen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Typen</SelectItem>
                  <SelectItem value="message_rate">Nachrichtenrate</SelectItem>
                  <SelectItem value="session_creation">Sitzungserstellung</SelectItem>
                  <SelectItem value="concurrent_session">Gleichzeitige Sitzungen</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="start-date">Von Datum</Label>
              <Input
                id="start-date"
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="end-date">Bis Datum</Label>
              <Input
                id="end-date"
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleApplyFilters} className="flex-1">
                <RefreshCw className="w-4 h-4 mr-2" />
                Anwenden
              </Button>
              <Button variant="outline" onClick={handleResetFilters}>
                Zurücksetzen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Violations Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Verstöße</CardTitle>
              <CardDescription>
                {loading ? 'Laden...' : isAgentView
                  ? `${total} Verstöße für ${agentName || 'diesen Agent'}`
                  : `${total} Verstöße insgesamt`}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="flex items-center justify-center p-8 text-red-600">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={loadViolations} className="ml-4">
                Erneut versuchen
              </Button>
            </div>
          )}

          {loading && !error && (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Verstöße werden geladen...</span>
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Zeitstempel</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">IP-Adresse</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Agent</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Verstoß-Typ</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Anzahl / Limit</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Kunde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {violations.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-gray-500">
                          Keine Verstöße gefunden
                        </td>
                      </tr>
                    )}
                    {violations.map((violation) => (
                      <tr key={violation.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-700 whitespace-nowrap">
                          {formatDate(violation.timestamp || violation.createdAt)}
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-mono text-sm text-gray-900">{violation.ip}</span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {violation.agentName ?? (violation.agentId ? violation.agentId : '-')}
                        </td>
                        <td className="py-3 px-4">
                          <Badge
                            className={
                              VIOLATION_TYPE_COLORS[violation.violationType] ?? 'bg-gray-100 text-gray-800'
                            }
                          >
                            {VIOLATION_TYPE_LABELS[violation.violationType] ?? violation.violationType}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          <span className="font-medium text-red-600">{violation.count}</span>
                          <span className="text-gray-400 mx-1">/</span>
                          <span>{violation.limit}</span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {violation.customerName ?? (violation.customerId ? violation.customerId : '-')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {total > 0 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Einträge pro Seite:</span>
                      <Select
                        value={limit.toString()}
                        onValueChange={(val) => {
                          setLimit(parseInt(val));
                          setPage(1);
                        }}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAGE_SIZE_OPTIONS.map((size) => (
                            <SelectItem key={size} value={size.toString()}>
                              {size}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <span className="text-sm text-gray-600">
                      {(page - 1) * limit + 1}–{Math.min(page * limit, total)} von {total}
                    </span>
                  </div>

                  <div className="flex items-center space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    {getPageNumbers().map((p, idx) =>
                      p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">
                          ...
                        </span>
                      ) : (
                        <Button
                          key={p}
                          variant={page === p ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPage(p as number)}
                          className="w-8"
                        >
                          {p}
                        </Button>
                      )
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
